<template>
  <p class="text-body text-muted">
    <template v-if="address">{{ address }} · </template>
    <span :class="['font-semibold', statusClass]">{{ ordering }}</span>
    <template v-if="modesLabel"> · {{ modesLabel }}</template>
  </p>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { OrderingMode } from '@/domains/pjazza/types/consts/OrderingMode.consts.ts'

import { isServing, orderingModesLabel, orderingStatusLabel } from '@/domains/pjazza/utils/venueOrderingStatus'

/**
 * Where a venue is, whether it is serving, and how you can order from it —
 * one line, not a row of pills.
 *
 * Address, status and the ordering modes used to each get a bordered chip of
 * their own, which meant five facts about one venue carried the same visual
 * weight as five different venues would. Read as a sentence instead: bold and
 * colour go on the one thing that changes state — "Taking orders now", or
 * "Taking bookings" at a venue that only takes bookings — and
 * everything else is just the words that follow it.
 *
 * No opening hours. Nothing in the back office can set them, so a line about
 * them would be an apology for a missing feature rather than a fact about the
 * venue — and "Taking orders now" already answers the question hours are
 * looked up to answer.
 */
const props = defineProps<{
  addressLine?: string | null
  locality?: string | null
  orderingMode: OrderingMode
  qrEnabled?: boolean
  takeawayEnabled?: boolean
  /** Set when a timed pause will lift by itself. */
  reopensAt?: string | null
}>()

const address = computed(() => [props.addressLine, props.locality].filter(Boolean).join(', '))

const ordering = computed(() => orderingStatusLabel(props.orderingMode, props.reopensAt))

const statusClass = computed(() => (isServing(props.orderingMode) ? 'text-ready' : 'text-ink'))

const modesLabel = computed(() => orderingModesLabel(props.qrEnabled, props.takeawayEnabled))
</script>
